/**
 * Account Types and Interfaces
 * Task 003 - Account Management
 */

export type AccountType = 'checking' | 'saving' | 'credit_card' | 'paylater' | 'loan' | 'cash' | 'e_wallet';
export type AccountStatus = 'active' | 'closed';

export interface Account {
  id: string;
  user_id: string;
  name: string;
  type: AccountType;
  balance: number;
  status: AccountStatus;
  billing_date?: number; // For credit cards only (1-28)
  created_at: string;
  updated_at: string;
}

export interface CreateAccountData {
  name: string;
  type: AccountType;
  balance: number;
  billing_date?: number; // For credit cards only (1-28)
}

export interface UpdateAccountData {
  name?: string;
  type?: AccountType;
  balance?: number;
  billing_date?: number; // For credit cards only (1-28)
}

export interface AccountTypeDisplay {
  code: AccountType;
  display_name_en: string;
  display_name_id: string;
  is_debt: boolean;
  name_placeholder: string;
}

// Account type display mappings
export const ACCOUNT_TYPE_DISPLAYS: Record<AccountType, AccountTypeDisplay> = {
  checking: {
    code: 'checking',
    display_name_en: 'Checking Account',
    display_name_id: 'Rekening untuk Transaksi',
    is_debt: false,
    name_placeholder: 'Contoh: Rekening Mandiri',
  },
  saving: {
    code: 'saving',
    display_name_en: 'Savings Account',
    display_name_id: 'Tabungan',
    is_debt: false,
    name_placeholder: 'Contoh: Tabungan ke Jepang',
  },
  credit_card: {
    code: 'credit_card',
    display_name_en: 'Credit Card',
    display_name_id: 'Kartu Kredit',
    is_debt: true,
    name_placeholder: 'Contoh: CC BCA',
  },
  loan: {
    code: 'loan',
    display_name_en: 'Loan',
    display_name_id: 'Pinjaman',
    is_debt: true,
    name_placeholder: 'Contoh: KPR',
  },
  cash: {
    code: 'cash',
    display_name_en: 'Cash',
    display_name_id: 'Tunai',
    is_debt: false,
    name_placeholder: 'Contoh: Cash di Dompet',
  },
  e_wallet: {
    code: 'e_wallet',
    display_name_en: 'E-Wallet',
    display_name_id: 'E-Wallet',
    is_debt: false,
    name_placeholder: 'Contoh: Gopay',
  },
  paylater: {
    code: 'paylater',
    display_name_en: 'Paylater',
    display_name_id: 'Paylater',
    is_debt: true,
    name_placeholder: 'Contoh: Shopee Paylater',
  },
};

export interface AccountCloseResponse {
  account: Account;
  zeroing_transaction_id: string | null;
  message: string;
}

export interface AccountTransactionCount {
  has_transactions: boolean;
  transaction_count: number;
}

export interface AccountContextType {
  accounts: Account[];
  closedAccounts: Account[];
  loading: boolean;
  error: string | null;
  totalPool: number;
  createAccount: (data: CreateAccountData) => Promise<void>;
  updateAccount: (accountId: string, data: UpdateAccountData) => Promise<void>;
  deleteAccount: (accountId: string) => Promise<void>;
  closeAccount: (accountId: string, monthPlanId: string) => Promise<AccountCloseResponse>;
  reopenAccount: (accountId: string) => Promise<void>;
  getTransactionCount: (accountId: string) => Promise<AccountTransactionCount>;
  refreshAccounts: () => Promise<void>;
  refreshClosedAccounts: () => Promise<void>;
  getAccountById: (accountId: string) => Account | undefined;
}
